import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
// import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import axios from "axios";
import { CUSTOMER_CARE_NUMBER, CUSTOMER_CARE_TEL } from "../constants";
import {
  OFFICE_ADDRESS,
  SUPPORT_EMAIL,
  SUPPORT_WHATSAPP,
  SUPPORT_WHATSAPP_DISPLAY,
} from "../config/market";

// Icons
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import PersonOffIcon from "@mui/icons-material/PersonOff";

// ───────────────────────────────────────────────────────────────
// Animations
// ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)   scale(1); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.6; }
`;

// ───────────────────────────────────────────────────────────────
// Layout
// ───────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: var(--lusuku-nav-h, 130px);

  @media (max-width: 850px) {
    padding-top: var(--lusuku-nav-h, 130px);
  }
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, #324d54 0%, #1a2a2e 100%);
  color: white;
  padding: 80px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    bottom: -50px;
    left: 0;
    right: 0;
    height: 100px;
    background: #f8f9fa;
    transform: skewY(-2deg);
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 16px;
  background: linear-gradient(to right, #ffffff, #a5d6a7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #e0f2f1;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: -40px auto 60px;
  padding: 0 20px;
  position: relative;
  z-index: 10;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: #324d54;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: ${(props) => props.color || "#324d54"};
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 30px;
`;

const ContactItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  background-color: #f8fbfb;
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  border: 1px solid #eef2f3;
  transition: all 0.3s ease;

  &:hover {
    background-color: white;
    border-color: #324d54;
    box-shadow: 0 5px 15px rgba(50, 77, 84, 0.1);
    transform: translateY(-3px);
  }

  svg {
    font-size: 2.5rem;
    color: #324d54;
    margin-bottom: 16px;
  }
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  color: #546e7a;
  margin-bottom: 8px;
`;

const Value = styled.div`
  font-weight: 700;
  font-size: 1.2rem;
  color: #324d54;
  text-align: center;
`;

// ───────────────────────────────────────────────────────────────
// Account Deletion Section
// ───────────────────────────────────────────────────────────────
const DeleteSection = styled.div`
  background-color: #fff5f5;
  border: 1px solid #ffebeb;
  border-radius: 16px;
  padding: 30px;
  margin-top: 40px;
  animation: ${fadeIn} 0.4s ease;
`;

const DeleteTitle = styled.h3`
  color: #c0392b;
  font-size: 1.4rem;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DeleteText = styled.p`
  color: #555;
  line-height: 1.7;
  margin-bottom: 16px;
  font-size: 0.98rem;
`;

const DeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #c0392b;
  color: white;
  padding: 13px 26px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #a93226;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(192, 57, 43, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// ───────────────────────────────────────────────────────────────
// Modal
// ───────────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 20px;
  padding: 36px 32px;
  max-width: 500px;
  width: 100%;
  position: relative;
  animation: ${slideUp} 0.3s ease;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);

  @media (max-width: 480px) {
    padding: 28px 20px;
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: #f5f5f5;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #eee;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: #c0392b;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalSubtitle = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 24px;
`;

const WarningBox = styled.div`
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 22px;
  display: flex;
  gap: 10px;
  align-items: flex-start;

  svg {
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    font-size: 0.88rem;
    color: #7c5a00;
    margin: 0;
    line-height: 1.5;
  }
`;

const SuccessBox = styled.div`
  text-align: center;
  padding: 20px 0;
  animation: ${fadeIn} 0.4s ease;

  svg {
    font-size: 4rem;
    color: #27ae60;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.4rem;
    color: #1a1a1a;
    margin: 0 0 10px;
  }

  p {
    color: #555;
    font-size: 0.95rem;
    line-height: 1.6;
    margin: 0 0 8px;
  }

  code {
    font-size: 0.8rem;
    color: #888;
    background: #f5f5f5;
    padding: 3px 8px;
    border-radius: 4px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 18px;

  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
  }

  input,
  textarea {
    width: 100%;
    padding: 11px 14px;
    border: 1.5px solid #ddd;
    border-radius: 10px;
    font-size: 0.95rem;
    color: #333;
    background: ${(props) => (props.readOnly ? "#f8f9fa" : "white")};
    box-sizing: border-box;
    transition: border-color 0.2s;
    font-family: inherit;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #c0392b;
    }

    &::placeholder {
      color: #aaa;
    }
  }
`;

const ErrorMsg = styled.p`
  color: #c0392b;
  font-size: 0.88rem;
  margin: 6px 0 0;
`;

const AuthBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 0.82rem;
  color: #2e7d32;
  font-weight: 600;
  margin-bottom: 18px;

  svg {
    font-size: 1rem;
  }
`;

const LoadingDots = styled.span`
  display: inline-block;
  animation: ${pulse} 1.2s ease infinite;
`;

// ───────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────
const Contact = () => {
  const { pathname } = useLocation();
  const currentUser = useSelector((state) => state.user?.currentUser);

  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState("form"); // "form" | "success"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Pre-fill from currentUser when modal opens
  useEffect(() => {
    if (showModal && currentUser) {
      // Resolve email from various auth response shapes
      const userEmail =
        currentUser.email ||
        currentUser.result?.email ||
        "";
      // Resolve phone
      const userPhone =
        currentUser.phoneNumber ||
        currentUser.result?.phoneNumber ||
        "";
      setEmail(userEmail);
      setPhoneNumber(userPhone);
    }
    if (!showModal) {
      // Reset on close
      setStep("form");
      setError("");
      setReason("");
      if (!currentUser) {
        setEmail("");
        setPhoneNumber("");
      }
    }
  }, [showModal, currentUser]);

  const isLoggedIn = Boolean(currentUser);

  // Resolve token: currentUser can have .token or .accessToken
  const resolveToken = () =>
    currentUser?.token || currentUser?.accessToken || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Guest validation
    if (!isLoggedIn && !email && !phoneNumber) {
      setError("Please enter your email address or phone number.");
      return;
    }

    setLoading(true);
    try {
      const payload = { platform: "web" };

      // Only include fields that are actually filled
      if (email.trim()) payload.email = email.trim();
      if (phoneNumber.trim()) payload.phoneNumber = phoneNumber.trim();
      if (reason.trim()) payload.reason = reason.trim();

      const headers = {};
      const token = resolveToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/user/request-account-deletion`,
        payload,
        { headers }
      );

      if (res.data.success) {
        setStep("success");
      } else {
        setError(res.data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <Navbar />

      <HeaderSection>
        <Title>Contact Us</Title>
        <Subtitle>
          We're here to help! Reach out to us for support, inquiries, or
          feedback.
        </Subtitle>
      </HeaderSection>

      <Container>
        {/* ── Get in Touch Card ── */}
        <Card>
          <SectionTitle color="#324d54">
            <SupportAgentIcon fontSize="large" />
            Get in Touch
          </SectionTitle>

          <ContactGrid>
            <ContactItem href={`mailto:${SUPPORT_EMAIL}`}>
              <EmailIcon />
              <Label>Email Support</Label>
              <Value>{SUPPORT_EMAIL}</Value>
            </ContactItem>

            {CUSTOMER_CARE_NUMBER && (
              <ContactItem href={CUSTOMER_CARE_TEL}>
                <PhoneIcon />
                <Label>Phone Support</Label>
                <Value>{CUSTOMER_CARE_NUMBER}</Value>
              </ContactItem>
            )}

            {SUPPORT_WHATSAPP && (
              <ContactItem href={`https://wa.me/${SUPPORT_WHATSAPP}`} target="_blank">
                <WhatsAppIcon />
                <Label>WhatsApp</Label>
                <Value>{SUPPORT_WHATSAPP_DISPLAY}</Value>
              </ContactItem>
            )}
          </ContactGrid>

          <div style={{ marginTop: "40px", textAlign: "center" }}>
            <Label>Visit Our Office</Label>
            <p style={{ fontSize: "1.1rem", color: "#333", lineHeight: "1.6" }}>
              <strong>Lusuku Fresh Services</strong>
              <br />
              {OFFICE_ADDRESS.map((line) => (
                <span key={line}>
                  {line}
                  <br />
                </span>
              ))}
            </p>
          </div>
        </Card>

        {/* ── Account Deletion Card ── */}
        <Card id="delete-account">
          <DeleteSection>
            <DeleteTitle>
              <DeleteForeverIcon />
              Account Deletion
            </DeleteTitle>

            <DeleteText>
              We value your privacy. If you wish to delete your account and
              remove all associated data from our systems, you can submit a
              request using the button below.
            </DeleteText>

            <DeleteText>
              <strong>What happens next?</strong>
              <br />
              Once we verify your identity, your account will be permanently
              deactivated, and all personal data (name, email, phone, order
              history) will be anonymized or deleted within{" "}
              <strong>30 days</strong>, except for data we are required to
              retain for legal or accounting purposes.
            </DeleteText>

            <DeleteButton
              id="request-account-deletion-btn"
              onClick={() => setShowModal(true)}
            >
              <PersonOffIcon fontSize="small" />
              Request Account Deletion
            </DeleteButton>
          </DeleteSection>
        </Card>
      </Container>

      {/* <Footer /> */}

      {/* ── Modal ── */}
      {showModal && (
        <Overlay onClick={() => !loading && setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalClose
              id="deletion-modal-close"
              onClick={() => !loading && setShowModal(false)}
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </ModalClose>

            {step === "success" ? (
              /* ── Success State ── */
              <SuccessBox>
                <CheckCircleOutlineIcon />
                <h3>Request Submitted</h3>
                <p>
                  Your account deletion request has been received. We'll
                  process it within <strong>30 days</strong>.
                </p>
                <p>
                  A confirmation email has been sent{" "}
                  {email ? `to ${email}` : "to you"}.
                </p>
                <p style={{ marginTop: "18px" }}>
                  <button
                    id="deletion-success-close-btn"
                    onClick={() => setShowModal(false)}
                    style={{
                      background: "#27ae60",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      padding: "12px 28px",
                      fontWeight: 700,
                      fontSize: "1rem",
                      cursor: "pointer",
                    }}
                  >
                    Done
                  </button>
                </p>
              </SuccessBox>
            ) : (
              /* ── Form State ── */
              <>
                <ModalTitle>
                  <DeleteForeverIcon />
                  Delete Account
                </ModalTitle>

                <ModalSubtitle>
                  This action is irreversible. Once processed, your account and
                  all associated data will be permanently deleted.
                </ModalSubtitle>

                {isLoggedIn && (
                  <AuthBadge>
                    <LockPersonIcon />
                    Signed in — your identity will be auto-verified
                  </AuthBadge>
                )}

                <WarningBox>
                  <WarningAmberIcon />
                  <p>
                    Upon deletion: orders, wallet balance, saved addresses, and
                    order history will be anonymized. This cannot be undone.
                  </p>
                </WarningBox>

                <form onSubmit={handleSubmit}>
                  {/* Email */}
                  <FormGroup readOnly={isLoggedIn && Boolean(email)}>
                    <label htmlFor="deletion-email">
                      Email Address{" "}
                      {!isLoggedIn && !phoneNumber && (
                        <span style={{ color: "#c0392b" }}>*</span>
                      )}
                    </label>
                    <input
                      id="deletion-email"
                      type="email"
                      placeholder={
                        isLoggedIn ? "Linked to your account" : "you@example.com"
                      }
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      readOnly={isLoggedIn && Boolean(email)}
                    />
                  </FormGroup>

                  {/* Phone — only show for guests or phone-only accounts */}
                  {(!isLoggedIn || (isLoggedIn && !email)) && (
                    <FormGroup>
                      <label htmlFor="deletion-phone">
                        Phone Number{" "}
                        {!email && !isLoggedIn && (
                          <span style={{ color: "#c0392b" }}>*</span>
                        )}
                      </label>
                      <input
                        id="deletion-phone"
                        type="tel"
                        placeholder="+256 700 000 000"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        readOnly={isLoggedIn && Boolean(phoneNumber)}
                      />
                    </FormGroup>
                  )}

                  {/* Reason */}
                  <FormGroup>
                    <label htmlFor="deletion-reason">
                      Reason{" "}
                      <span style={{ color: "#aaa", fontWeight: 400 }}>
                        (optional)
                      </span>
                    </label>
                    <textarea
                      id="deletion-reason"
                      rows={3}
                      placeholder="Tell us why you'd like to delete your account…"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </FormGroup>

                  {error && <ErrorMsg>{error}</ErrorMsg>}

                  <DeleteButton
                    id="deletion-confirm-btn"
                    type="submit"
                    disabled={loading}
                    style={{ width: "100%", justifyContent: "center", marginTop: "8px" }}
                  >
                    {loading ? (
                      <>
                        Submitting
                        <LoadingDots>…</LoadingDots>
                      </>
                    ) : (
                      <>
                        <DeleteForeverIcon fontSize="small" />
                        Confirm Deletion Request
                      </>
                    )}
                  </DeleteButton>
                </form>
              </>
            )}
          </Modal>
        </Overlay>
      )}
    </PageWrapper>
  );
};

export default Contact;
